import React, { useEffect, useMemo, useState } from "react";
import "chart.js/auto";
import "../../../styles/common/dashboardPage.scss";
import "../../../styles/common/dashboardOverviewSection.scss";
import "../../../styles/common/dashboardCourseSection.scss";
import Cookies from "js-cookie";
import GlobalLoader from "../../common/GlobalLoader";
import ChartCard from "../../common/charts/ChartCard";
import BarChart from "../../common/charts/BarChart";
import DoughnutChart from "../../common/charts/DoughnutChart";
import LineChart from "../../common/charts/LineChart";
import AreaChart from "../../common/charts/AreaChart";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GroupWorkOutlinedIcon from "@mui/icons-material/GroupWorkOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import { getAdminCumulativeDashboard } from "../../../helper/apicalls/dashboard";
import TooltipText from "../../common/tooltipText";

const dedupeByInstitutionId = (arr) => {
  if (!Array.isArray(arr)) return [];
  const map = new Map();
  for (const item of arr) {
    if (!item) continue;
    const id = item.institution_id ?? item.institutionId ?? item.id;
    if (!id) continue;
    if (!map.has(id)) {
      map.set(id, item);
    }
  }
  return Array.from(map.values());
};

const toInstitutionName = (item) => {
  return (
    item?.institution_name ||
    item?.institutionName ||
    item?.name ||
    "Unnamed Institution"
  );
};

const AdminDashboardCumulativePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const chartPalette = useMemo(
    () => ({
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
      soft: {
        orange: "rgba(252, 164, 59, 0.16)",
        mint: "rgba(133, 238, 173, 0.16)",
        lime: "rgba(212, 249, 45, 0.16)",
        yellow: "rgba(250, 238, 112, 0.16)",
        slate: "rgba(148, 163, 184, 0.16)",
      },
      accents: [
        "rgba(252, 164, 59, 0.85)",
        "rgba(133, 238, 173, 0.85)",
        "rgba(212, 249, 45, 0.85)",
        "rgba(250, 238, 112, 0.85)",
        "rgba(148, 163, 184, 0.65)",
      ],
      accentsHover: [
        "rgba(252, 164, 59, 0.95)",
        "rgba(133, 238, 173, 0.95)",
        "rgba(212, 249, 45, 0.95)",
        "rgba(250, 238, 112, 0.95)",
        "rgba(148, 163, 184, 0.85)",
      ],
    }),
    [],
  );

  const sliceColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => chartPalette.accents[i % chartPalette.accents.length],
    );

  useEffect(() => {
    const fetchCumulative = async () => {
      const token = Cookies.get("admin_token");
      if (!token) {
        setError("Missing admin token");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const res = await getAdminCumulativeDashboard(token);
        setData(res || null);
      } catch (e) {
        console.error("Failed to load cumulative dashboard data", e);
        setError("Failed to load cumulative dashboard data");
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCumulative();
  }, []);

  const overall = data?.overall || {};

  const engagementByInstitution = dedupeByInstitutionId(
    data?.distributions?.engagement_by_institution,
  );
  const homeworkByInstitution = dedupeByInstitutionId(
    data?.distributions?.homework_completion_by_institution,
  );
  const testPerfByInstitution = dedupeByInstitutionId(
    data?.distributions?.test_performance_by_institution,
  );

  const activeRecallByInstitution = dedupeByInstitutionId(
    data?.distributions?.active_recall_by_institution,
  );
  const adaptiveByInstitution = dedupeByInstitutionId(
    data?.distributions?.adaptive_learning_by_institution,
  );
  const materialsByInstitution = dedupeByInstitutionId(
    data?.distributions?.materials_by_institution,
  );
  const quizByInstitution = dedupeByInstitutionId(
    data?.distributions?.quiz_by_institution,
  );
  const gamificationByInstitution = dedupeByInstitutionId(
    data?.distributions?.gamification_by_institution,
  );

  const leaderboardStudentCount = dedupeByInstitutionId(
    data?.leaderboards?.scale?.student_count,
  );
  const leaderboardTeacherCount = dedupeByInstitutionId(
    data?.leaderboards?.scale?.teacher_count,
  );

  const leaderboardHw = dedupeByInstitutionId(
    data?.leaderboards?.performance?.homework_completion,
  );
  const leaderboardPerf = dedupeByInstitutionId(
    data?.leaderboards?.performance?.overall,
  );

  const gamificationCoins = dedupeByInstitutionId(
    data?.leaderboards?.gamification?.total_coins ||
      data?.gamification?.total_coins,
  );
  const gamificationXp = dedupeByInstitutionId(
    data?.leaderboards?.gamification?.total_xp || data?.gamification?.total_xp,
  );

  const activeVsTotalInstitutions = {
    active: overall.total_active_institutions ?? 0,
    total: overall.total_institutions ?? 0,
  };

  const activeInstitutionCount = Math.min(
    Number(activeVsTotalInstitutions.active) || 0,
    Number(activeVsTotalInstitutions.total) || 0,
  );
  const inactiveInstitutionCount = Math.max(
    (Number(activeVsTotalInstitutions.total) || 0) - activeInstitutionCount,
    0,
  );

  const pct = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return 0;
    return Math.max(0, Math.min(100, num));
  };

  const institutionIds = Array.from(
    new Set(
      [
        ...engagementByInstitution.map((i) => i.institution_id),
        ...homeworkByInstitution.map((i) => i.institution_id),
        ...testPerfByInstitution.map((i) => i.institution_id),
        ...activeRecallByInstitution.map((i) => i.institution_id),
        ...adaptiveByInstitution.map((i) => i.institution_id),
        ...materialsByInstitution.map((i) => i.institution_id),
        ...quizByInstitution.map((i) => i.institution_id),
        ...gamificationByInstitution.map((i) => i.institution_id),
        ...leaderboardStudentCount.map((i) => i.institution_id),
        ...leaderboardTeacherCount.map((i) => i.institution_id),
        ...gamificationCoins.map((i) => i.institution_id),
        ...gamificationXp.map((i) => i.institution_id),
      ].filter(Boolean),
    ),
  );

  const institutionIndex = institutionIds
    .map((id) => {
      const studentRow =
        leaderboardStudentCount.find((r) => r.institution_id === id) || null;
      const teacherRow =
        leaderboardTeacherCount.find((r) => r.institution_id === id) || null;
      const engagementRow =
        engagementByInstitution.find((r) => r.institution_id === id) || null;
      const homeworkRow =
        homeworkByInstitution.find((r) => r.institution_id === id) || null;
      const testRow =
        testPerfByInstitution.find((r) => r.institution_id === id) || null;
      const recallRow =
        activeRecallByInstitution.find((r) => r.institution_id === id) || null;
      const adaptiveRow =
        adaptiveByInstitution.find((r) => r.institution_id === id) || null;
      const materialsRow =
        materialsByInstitution.find((r) => r.institution_id === id) || null;
      const quizRow =
        quizByInstitution.find((r) => r.institution_id === id) || null;
      const gamificationRow =
        gamificationByInstitution.find((r) => r.institution_id === id) || null;
      const coinsRow =
        gamificationCoins.find((r) => r.institution_id === id) || null;
      const xpRow = gamificationXp.find((r) => r.institution_id === id) || null;

      const anyRow = studentRow ||
        teacherRow ||
        engagementRow ||
        homeworkRow ||
        testRow ||
        recallRow ||
        adaptiveRow ||
        materialsRow ||
        quizRow ||
        gamificationRow ||
        coinsRow ||
        xpRow || { institution_id: id };

      return {
        institution_id: id,
        institution_name: toInstitutionName(anyRow),
        student_count: studentRow?.student_count ?? 0,
        teacher_count: teacherRow?.teacher_count ?? 0,
        active_students: engagementRow?.active_students ?? 0,
        assigned: homeworkRow?.assigned ?? 0,
        completed: homeworkRow?.completed ?? 0,
        average_test_score: testRow?.average_test_score ?? 0,
        tests_conducted: testRow?.tests_conducted ?? 0,
        total_active_recall: recallRow?.total_active_recall ?? 0,
        active_recall_accuracy: recallRow?.active_recall_accuracy ?? 0,
        total_adaptive_tests: adaptiveRow?.total_adaptive_tests ?? 0,
        adaptive_accuracy: adaptiveRow?.adaptive_accuracy ?? 0,
        adaptive_average_marks: adaptiveRow?.adaptive_average_marks ?? 0,
        total_materials_uploaded: materialsRow?.total_materials_uploaded ?? 0,
        avg_materials_per_student: materialsRow?.avg_materials_per_student ?? 0,
        quiz_pages_assigned: quizRow?.quiz_pages_assigned ?? 0,
        quiz_pages_completed: quizRow?.quiz_pages_completed ?? 0,
        quiz_accuracy: quizRow?.quiz_accuracy ?? 0,
        quiz_completion_rate: quizRow?.quiz_completion_rate ?? 0,
        total_coins: gamificationRow?.total_coins ?? coinsRow?.total_coins ?? 0,
        avg_coins_per_student:
          gamificationRow?.avg_coins_per_student ??
          coinsRow?.avg_coins_per_student ??
          0,
        total_xp: gamificationRow?.total_xp ?? xpRow?.total_xp ?? 0,
        avg_xp_per_student:
          gamificationRow?.avg_xp_per_student ?? xpRow?.avg_xp_per_student ?? 0,
        total_courses: gamificationRow?.total_courses ?? 0,
      };
    })
    .sort((a, b) =>
      String(a?.institution_name || "").localeCompare(
        String(b?.institution_name || ""),
      ),
    );

  const institutionLabels = institutionIndex.map((i) => i.institution_name);

  const clamp100 = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return 0;
    return Math.max(0, Math.min(100, num));
  };

  return (
    <div className="dashboard-page">
      {loading && <GlobalLoader visible={loading} />}
      {!!error && <div className="dashboard-error">{error}</div>}

      <h1>Dashboard</h1>

      <section className="dashboard-overview-section">
        <h2>Overview</h2>
        <div className="overview-grid">
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Total Institutions"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overall.total_institutions ?? 0}</strong>
              </div>
              <SchoolOutlinedIcon
                className="overview-card-icon"
                sx={{ color: "#FCA43B !important" }}
              />
            </div>
          </div>

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Total Institution Admins"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overall.total_institution_admins ?? 0}</strong>
              </div>
              <AdminPanelSettingsOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Total Students" className="overview-card-label" fullWidth />
                <strong>{overall.total_students ?? 0}</strong>
              </div>
              <GroupsOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Total Teachers" className="overview-card-label" fullWidth />
                <strong>{overall.total_teachers ?? 0}</strong>
              </div>
              <PersonOutlineOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Total Batches" className="overview-card-label" fullWidth />
                <strong>{overall.total_batches ?? 0}</strong>
              </div>
              <GroupWorkOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Total Tests Conducted"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overall.total_tests_conducted ?? 0}</strong>
              </div>
              <QuizOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Homework Assigned" className="overview-card-label" fullWidth />
                <strong>{overall.total_homework_assigned ?? 0}</strong>
              </div>
              <AssignmentOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Homework Completed" className="overview-card-label" fullWidth />
                <strong>{overall.total_homework_completed ?? 0}</strong>
              </div>
              <TaskAltOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Active Institutions" className="overview-card-label" fullWidth />
                <strong>{overall.total_active_institutions ?? 0}</strong>
              </div>
              <SchoolOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Avg Test Score"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overall.global_average_test_score ?? 0}</strong>
              </div>
              <TrendingUpOutlinedIcon
                className="overview-card-icon"
                sx={{ color: "#85EEAD !important" }}
              />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Homework Completion"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{pct(overall.global_homework_completion_rate)}</strong>
              </div>
              <PercentOutlinedIcon
                className="overview-card-icon"
                sx={{ color: "#D4F92D !important" }}
              />
            </div>
          </div>

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Total Active Recall" className="overview-card-label" fullWidth />
                <strong>{overall.total_active_recall ?? 0}</strong>
              </div>
              <TaskAltOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Global Active Recall Accuracy"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{pct(overall.global_active_recall_accuracy)}</strong>
              </div>
              <PercentOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Total Adaptive Tests" className="overview-card-label" fullWidth />
                <strong>{overall.total_adaptive_tests ?? 0}</strong>
              </div>
              <QuizOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Global Adaptive Accuracy"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{pct(overall.global_adaptive_accuracy)}</strong>
              </div>
              <PercentOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Global Adaptive Avg Marks"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overall.global_adaptive_average_marks ?? 0}</strong>
              </div>
              <TrendingUpOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Total Materials Uploaded"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overall.total_materials_uploaded ?? 0}</strong>
              </div>
              <AssignmentOutlinedIcon
                className="overview-card-icon"
                sx={{ color: "#FAEE70 !important" }}
              />
            </div>
          </div>

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Quiz Pages Assigned"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overall.total_quiz_pages_assigned ?? 0}</strong>
              </div>
              <AssignmentOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Quiz Pages Completed"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overall.total_quiz_pages_completed ?? 0}</strong>
              </div>
              <TaskAltOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Global Quiz Accuracy" className="overview-card-label" fullWidth />
                <strong>{pct(overall.global_quiz_accuracy)}</strong>
              </div>
              <PercentOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}

          {/*
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Global Quiz Completion"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{pct(overall.global_quiz_completion_rate)}%</strong>
              </div>
              <PercentOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Total Coin Policies" className="overview-card-label" fullWidth />
                <strong>{overall.total_coin_policies ?? 0}</strong>
              </div>
              <TrendingUpOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText text="Total XP Policies" className="overview-card-label" fullWidth />
                <strong>{overall.total_xp_policies ?? 0}</strong>
              </div>
              <TrendingUpOutlinedIcon className="overview-card-icon" />
            </div>
          </div>
          */}
        </div>
      </section>

      <section className="dashboard-course-section">
        <h2>Institutions Breakdown</h2>
        <div className="dashboard-course-grid">
          {institutionIndex.length > 0 && (
            <ChartCard title="Students vs Teachers by Institution">
              <BarChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Students",
                      data: institutionIndex.map((i) => i.student_count ?? 0),
                      backgroundColor: chartPalette.solid.mint,
                      hoverBackgroundColor: chartPalette.hover.mint,
                      borderRadius: 10,
                    },
                    {
                      label: "Teachers",
                      data: institutionIndex.map((i) => i.teacher_count ?? 0),
                      backgroundColor: chartPalette.solid.yellow,
                      hoverBackgroundColor: chartPalette.hover.yellow,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>
          )}

          {activeRecallByInstitution.length > 0 && (
            <ChartCard
              title="Active Recall: Total Usage vs Accuracy by Institution"
              chips={[
                `Overall Accuracy: ${pct(overall.global_active_recall_accuracy)}%`,
              ]}
            >
              <LineChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Total Active Recall",
                      data: institutionIndex.map(
                        (i) => i.total_active_recall ?? 0,
                      ),
                      borderColor: chartPalette.solid.lime,
                      backgroundColor: chartPalette.soft.lime,
                      pointBackgroundColor: chartPalette.solid.lime,
                      pointHoverBackgroundColor: chartPalette.hover.lime,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                      yAxisID: "y",
                    },
                    {
                      label: "Accuracy (%)",
                      data: institutionIndex.map((i) =>
                        clamp100(i.active_recall_accuracy ?? 0),
                      ),
                      borderColor: chartPalette.solid.mint,
                      backgroundColor: chartPalette.soft.mint,
                      pointBackgroundColor: chartPalette.solid.mint,
                      pointHoverBackgroundColor: chartPalette.hover.mint,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                      yAxisID: "y1",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                  scales: {
                    y: { beginAtZero: true, position: "left" },
                    y1: {
                      beginAtZero: true,
                      position: "right",
                      max: 100,
                      grid: { drawOnChartArea: false },
                    },
                  },
                }}
              />
            </ChartCard>
          )}

          {adaptiveByInstitution.length > 0 && (
            <ChartCard
              title="Adaptive Learning: Tests vs Accuracy by Institution"
              chips={[
                `Overall Accuracy: ${pct(overall.global_adaptive_accuracy)}%`,
              ]}
            >
              <LineChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Total Adaptive Tests",
                      data: institutionIndex.map(
                        (i) => i.total_adaptive_tests ?? 0,
                      ),
                      borderColor: chartPalette.solid.orange,
                      backgroundColor: chartPalette.soft.orange,
                      pointBackgroundColor: chartPalette.solid.orange,
                      pointHoverBackgroundColor: chartPalette.hover.orange,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                      yAxisID: "y",
                    },
                    {
                      label: "Adaptive Accuracy (%)",
                      data: institutionIndex.map((i) =>
                        clamp100(i.adaptive_accuracy ?? 0),
                      ),
                      borderColor: chartPalette.solid.lime,
                      backgroundColor: chartPalette.soft.lime,
                      pointBackgroundColor: chartPalette.solid.lime,
                      pointHoverBackgroundColor: chartPalette.hover.lime,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                      yAxisID: "y1",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                  scales: {
                    y: { beginAtZero: true, position: "left" },
                    y1: {
                      beginAtZero: true,
                      position: "right",
                      max: 100,
                      grid: { drawOnChartArea: false },
                    },
                  },
                }}
              />
            </ChartCard>
          )}

          {materialsByInstitution.length > 0 && (
            <ChartCard title="Materials: Uploaded vs Avg per Student by Institution">
              <BarChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Total Materials Uploaded",
                      data: institutionIndex.map(
                        (i) => i.total_materials_uploaded ?? 0,
                      ),
                      backgroundColor: chartPalette.solid.mint,
                      hoverBackgroundColor: chartPalette.hover.mint,
                      borderRadius: 10,
                    },
                    {
                      label: "Avg Materials / Student",
                      data: institutionIndex.map(
                        (i) => i.avg_materials_per_student ?? 0,
                      ),
                      backgroundColor: chartPalette.solid.yellow,
                      hoverBackgroundColor: chartPalette.hover.yellow,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>
          )}

          {quizByInstitution.length > 0 && (
            <ChartCard
              title="Quiz: Pages vs Accuracy by Institution"
              chips={[
                `Overall Accuracy: ${pct(overall.global_quiz_accuracy)}%`,
              ]}
            >
              <LineChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Quiz Pages Assigned",
                      data: institutionIndex.map(
                        (i) => i.quiz_pages_assigned ?? 0,
                      ),
                      borderColor: chartPalette.solid.mint,
                      backgroundColor: chartPalette.soft.mint,
                      pointBackgroundColor: chartPalette.solid.mint,
                      pointHoverBackgroundColor: chartPalette.hover.mint,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                      yAxisID: "y",
                    },
                    {
                      label: "Quiz Pages Completed",
                      data: institutionIndex.map(
                        (i) => i.quiz_pages_completed ?? 0,
                      ),
                      borderColor: chartPalette.solid.orange,
                      backgroundColor: chartPalette.soft.orange,
                      pointBackgroundColor: chartPalette.solid.orange,
                      pointHoverBackgroundColor: chartPalette.hover.orange,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                      yAxisID: "y",
                    },
                    {
                      label: "Quiz Accuracy (%)",
                      data: institutionIndex.map((i) =>
                        clamp100(i.quiz_accuracy ?? 0),
                      ),
                      borderColor: chartPalette.solid.lime,
                      backgroundColor: chartPalette.soft.lime,
                      pointBackgroundColor: chartPalette.solid.lime,
                      pointHoverBackgroundColor: chartPalette.hover.lime,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                      yAxisID: "y1",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                  scales: {
                    y: { beginAtZero: true, position: "left" },
                    y1: {
                      beginAtZero: true,
                      position: "right",
                      max: 100,
                      grid: { drawOnChartArea: false },
                    },
                  },
                }}
              />
            </ChartCard>
          )}

          {testPerfByInstitution.length > 0 && (
            <ChartCard title="Tests Conducted & Avg Test Score by Institution">
              <LineChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Tests Conducted",
                      data: institutionIndex.map((i) => i.tests_conducted ?? 0),
                      borderColor: chartPalette.solid.orange,
                      backgroundColor: chartPalette.soft.orange,
                      pointBackgroundColor: chartPalette.solid.orange,
                      pointHoverBackgroundColor: chartPalette.hover.orange,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                    },
                    {
                      label: "Avg Test Score",
                      data: institutionIndex.map((i) =>
                        clamp100(i.average_test_score ?? 0),
                      ),
                      borderColor: chartPalette.solid.mint,
                      backgroundColor: chartPalette.soft.mint,
                      pointBackgroundColor: chartPalette.solid.mint,
                      pointHoverBackgroundColor: chartPalette.hover.mint,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      tension: 0.35,
                      pointRadius: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>
          )}

          {homeworkByInstitution.length > 0 && (
            <ChartCard title="Homework Assigned vs Completed by Institution">
              <AreaChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Assigned",
                      data: institutionIndex.map((i) => i.assigned ?? 0),
                      borderColor: chartPalette.solid.orange,
                      backgroundColor: chartPalette.soft.orange,
                      pointBackgroundColor: chartPalette.solid.orange,
                      pointHoverBackgroundColor: chartPalette.hover.orange,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      fill: true,
                    },
                    {
                      label: "Completed",
                      data: institutionIndex.map((i) => i.completed ?? 0),
                      borderColor: chartPalette.solid.mint,
                      backgroundColor: chartPalette.soft.mint,
                      pointBackgroundColor: chartPalette.solid.mint,
                      pointHoverBackgroundColor: chartPalette.hover.mint,
                      pointBorderColor: "rgba(15, 23, 42, 1)",
                      pointHoverBorderColor: "rgba(15, 23, 42, 1)",
                      fill: true,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { display: true, position: "bottom" } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>
          )}

          {engagementByInstitution.length > 0 && (
            <ChartCard title="Active vs Inactive Students by Institution">
              <BarChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Active Students",
                      data: institutionIndex.map((i) => i.active_students ?? 0),
                      backgroundColor: chartPalette.solid.mint,
                      hoverBackgroundColor: chartPalette.hover.mint,
                      borderRadius: 10,
                    },
                    {
                      label: "Inactive Students",
                      data: institutionIndex.map((i) => {
                        const total = Number(i?.student_count) || 0;
                        const active = Number(i?.active_students) || 0;
                        return Math.max(0, total - active);
                      }),
                      backgroundColor: chartPalette.soft.slate,
                      hoverBackgroundColor: chartPalette.soft.slate,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true, beginAtZero: true },
                  },
                }}
              />
            </ChartCard>
          )}

          {(gamificationByInstitution.length > 0 ||
            gamificationCoins.length > 0 ||
            gamificationXp.length > 0) && (
            <ChartCard
              title="Gamification: Total Coins vs Total XP by Institution"
              className="chart-card--full"
            >
              <BarChart
                data={{
                  labels: institutionLabels,
                  datasets: [
                    {
                      label: "Total Coins",
                      data: institutionIndex.map((i) => i.total_coins ?? 0),
                      backgroundColor: chartPalette.solid.yellow,
                      hoverBackgroundColor: chartPalette.hover.yellow,
                      borderRadius: 10,
                    },
                    {
                      label: "Total XP",
                      data: institutionIndex.map((i) => i.total_xp ?? 0),
                      backgroundColor: chartPalette.solid.lime,
                      hoverBackgroundColor: chartPalette.hover.lime,
                      borderRadius: 10,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "bottom" } },
                  scales: { y: { beginAtZero: true } },
                }}
              />
            </ChartCard>
          )}
        </div>
      </section>

      <section className="dashboard-course-section">
        <h2>Leaderboards</h2>
        <div className="dashboard-course-grid">
          {/* <ChartCard title="Institutions - Active vs Inactive">
            <DoughnutChart
              data={{
                labels: ["Active", "Inactive"],
                datasets: [
                  {
                    data: [activeInstitutionCount, inactiveInstitutionCount],
                    backgroundColor: [chartPalette.solid.mint, chartPalette.solid.slate],
                    hoverBackgroundColor: [chartPalette.hover.mint, chartPalette.hover.slate],
                    borderColor: "rgba(15, 23, 42, 1)",
                    borderWidth: 2,
                  },
                ],
              }}
              options={{ plugins: { legend: { position: "bottom" } } }}
            />
          </ChartCard>

          <ChartCard title="Top by Homework Completion Rate">
            <BarChart
              data={{
                labels: leaderboardHw.map((i) => toInstitutionName(i)),
                datasets: [
                  {
                    label: "Completion Rate (%)",
                    data: leaderboardHw.map((i) => clamp100(i.homework_completion_rate ?? 0)),
                    backgroundColor: chartPalette.solid.mint,
                    hoverBackgroundColor: chartPalette.hover.mint,
                    borderRadius: 10,
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: { legend: { position: "bottom" } },
                scales: { y: { beginAtZero: true, max: 100 } },
              }}
            />
          </ChartCard> */}

          <ChartCard title="Top by Performance Rate">
            <BarChart
              data={{
                labels: leaderboardPerf.map((i) => toInstitutionName(i)),
                datasets: [
                  {
                    label: "Performance Rate",
                    data: leaderboardPerf.map((i) =>
                      clamp100(i.performance_rate ?? 0),
                    ),
                    backgroundColor: chartPalette.solid.orange,
                    hoverBackgroundColor: chartPalette.hover.orange,
                    borderRadius: 10,
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: { legend: { position: "bottom" } },
                scales: { y: { beginAtZero: true, max: 100 } },
              }}
            />
          </ChartCard>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboardCumulativePage;
